import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DeviceMobile, PaperPlaneTilt, ClockCountdown, Plus, ArrowsClockwise, Trash, Bell } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EsendexPanel({ lang, onClose }) {
  const [tab, setTab] = useState('send');
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [balance, setBalance] = useState('?');
  const [loading, setLoading] = useState(false);
  const [reminderCfg, setReminderCfg] = useState({ enabled: false, hours_before: 24, message: '' });
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);

  // Send SMS state
  const [smsPhone, setSmsPhone] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [sending, setSending] = useState(false);

  const lb = lang === 'es' ? {
    send: 'Enviar SMS', templates: 'Plantillas', history: 'Historial', reminders: 'Recordatorios',
    phone: 'Telefono', message: 'Mensaje', sendBtn: 'Enviar', sending: 'Enviando...',
    balance: 'Saldo', sms: 'SMS', create: 'Crear', name: 'Nombre', body: 'Texto',
    cancel: 'Cancelar', save: 'Guardar', delete: 'Eliminar', refresh: 'Actualizar',
    noTemplates: 'No hay plantillas', noMessages: 'No hay mensajes',
    reminderEnabled: 'Recordatorios activos', hoursBefore: 'Horas antes de la cita',
    reminderMsg: 'Mensaje del recordatorio', saveConfig: 'Guardar configuracion',
    configSaved: 'Configuracion guardada', smsSent: 'SMS enviado',
    templateCreated: 'Plantilla creada', templateDeleted: 'Plantilla eliminada',
    tags: 'Tags: {nombre}, {fecha}, {hora}',
    phonePlaceholder: '+34612345678 (separar con coma si son varios)',
    loading: 'Cargando...',
    createTemplate: 'Crear Plantilla',
  } : {
    send: 'Send SMS', templates: 'Templates', history: 'History', reminders: 'Reminders',
    phone: 'Phone', message: 'Message', sendBtn: 'Send', sending: 'Sending...',
    balance: 'Balance', sms: 'SMS', create: 'Create', name: 'Name', body: 'Text',
    cancel: 'Cancel', save: 'Save', delete: 'Delete', refresh: 'Refresh',
    noTemplates: 'No templates', noMessages: 'No messages',
    reminderEnabled: 'Reminders active', hoursBefore: 'Hours before appointment',
    reminderMsg: 'Reminder message', saveConfig: 'Save configuration',
    configSaved: 'Configuration saved', smsSent: 'SMS sent',
    templateCreated: 'Template created', templateDeleted: 'Template deleted',
    tags: 'Tags: {nombre}, {fecha}, {hora}',
    phonePlaceholder: '+34612345678 (separar con coma si son varios)',
    loading: 'Loading...',
    createTemplate: 'Create Template',
  };

  const loadBalance = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/esendex/balance`, { withCredentials: true });
      setBalance(r.data?.data?.available || '?');
    } catch { setBalance('?'); }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/esendex/templates`, { withCredentials: true });
      const d = r.data?.data;
      setTemplates(Array.isArray(d) ? d : (d?.templates || []));
    } catch { setTemplates([]); }
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/esendex/messages`, { withCredentials: true });
      const d = r.data?.data;
      setMessages(Array.isArray(d) ? d : (d?.messages || []));
    } catch { setMessages([]); }
    setLoading(false);
  }, []);

  const loadReminderConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/esendex/reminder-config`, { withCredentials: true });
      setReminderCfg(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadBalance(); loadReminderConfig(); }, [loadBalance, loadReminderConfig]);

  useEffect(() => {
    if (tab === 'templates') loadTemplates();
    if (tab === 'history') loadMessages();
  }, [tab, loadTemplates, loadMessages]);

  const handleSendSms = async () => {
    if (!smsPhone.trim() || !smsBody.trim()) { toast.error(lang === 'es' ? 'Telefono y mensaje obligatorios' : 'Phone and message required'); return; }
    setSending(true);
    try {
      const phones = smsPhone.split(/[,;\s]+/).map(p => p.trim()).filter(p => p.length >= 6);
      if (!phones.length) { toast.error(lang === 'es' ? 'Telefono invalido' : 'Invalid phone'); setSending(false); return; }
      await axios.post(`${API_URL}/api/esendex/send`, { to: phones, body: smsBody }, { withCredentials: true });
      toast.success(lb.smsSent);
      setSmsPhone(''); setSmsBody('');
      loadBalance();
    } catch (err) { toast.error(errText(err)); }
    setSending(false);
  };

  const handleSaveReminderConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/esendex/reminder-config`, reminderCfg, { withCredentials: true });
      toast.success(lb.configSaved);
    } catch (err) { toast.error(errText(err)); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/esendex/templates/${id}`, { withCredentials: true });
      toast.success(lb.templateDeleted);
      loadTemplates();
    } catch (err) { toast.error(errText(err)); }
  };

  const handleClearHistory = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/esendex/messages/clear`, {}, { withCredentials: true });
      toast.success(lang === 'es' ? `${r.data.deleted} mensajes eliminados` : `${r.data.deleted} messages deleted`);
      setMessages([]);
    } catch (err) { toast.error(errText(err)); }
    setLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="esendex-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DeviceMobile size={20} weight="duotone" className="text-amber-600" />
          <span className="text-sm font-semibold text-stone-800 uppercase tracking-wider">Esendex SMS</span>
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
            {lb.balance}: {balance} {lb.sms}
          </Badge>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} className="text-stone-400 hover:text-stone-600">&times;</Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-stone-100 w-full justify-start">
          <TabsTrigger value="send" className="gap-1.5 text-xs" data-testid="sms-tab-send">
            <PaperPlaneTilt size={14} /> {lb.send}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5 text-xs" data-testid="sms-tab-templates">
            <DeviceMobile size={14} /> {lb.templates}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5 text-xs" data-testid="sms-tab-history">
            <ClockCountdown size={14} /> {lb.history}
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-1.5 text-xs" data-testid="sms-tab-reminders">
            <Bell size={14} /> {lb.reminders}
          </TabsTrigger>
        </TabsList>

        {/* SEND TAB */}
        <TabsContent value="send" className="mt-4">
          <Card className="border-stone-200 rounded-sm shadow-none">
            <CardContent className="p-4 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.phone}</Label>
                <Input data-testid="sms-phone" value={smsPhone} onChange={e => setSmsPhone(e.target.value)} placeholder={lb.phonePlaceholder} className="max-w-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.message}</Label>
                <textarea data-testid="sms-body" value={smsBody} onChange={e => setSmsBody(e.target.value)}
                  className="w-full min-h-[100px] text-xs border border-stone-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  placeholder={lang === 'es' ? 'Escribe tu mensaje...' : 'Write your message...'} />
                <p className="text-[10px] text-stone-400">{smsBody.length}/160 {lang === 'es' ? 'caracteres' : 'characters'} ({Math.ceil((smsBody.length || 1) / 160)} SMS)</p>
              </div>
              <Button onClick={handleSendSms} disabled={sending} className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1.5" data-testid="sms-send-btn">
                <PaperPlaneTilt size={14} /> {sending ? lb.sending : lb.sendBtn}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={loadTemplates} disabled={loading} className="gap-1.5 text-xs">
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> {lb.refresh}
            </Button>
            <Button size="sm" onClick={() => setShowCreateTemplate(true)} className="gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="sms-create-template">
              <Plus size={12} /> {lb.createTemplate}
            </Button>
          </div>
          {loading ? (
            <p className="text-xs text-stone-400 p-4">{lb.loading}</p>
          ) : templates.length === 0 ? (
            <p className="text-xs text-stone-400 p-4">{lb.noTemplates}</p>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} data-testid={`sms-template-${t.id}`} className="p-3 border border-stone-200 rounded hover:bg-stone-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-stone-800">{t.name}</span>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setSmsBody(t.body || ''); setTab('send'); }} className="h-7 text-[11px] text-amber-600 hover:text-amber-700 gap-1">
                        <PaperPlaneTilt size={12} /> {lang === 'es' ? 'Usar' : 'Use'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(t.id)} className="h-7 w-7 p-0 text-stone-400 hover:text-red-600">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-500 mt-1">{t.body}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={loadMessages} disabled={loading} className="gap-1.5 text-xs">
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> {lb.refresh}
            </Button>
            {messages.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleClearHistory} disabled={loading} className="gap-1.5 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="sms-clear-history">
                <Trash size={12} /> {lang === 'es' ? 'Limpiar historial' : 'Clear history'}
              </Button>
            )}
          </div>
          {loading ? (
            <p className="text-xs text-stone-400 p-4">{lb.loading}</p>
          ) : messages.length === 0 ? (
            <p className="text-xs text-stone-400 p-4">{lb.noMessages}</p>
          ) : (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {messages.map((m, i) => (
                <div key={m.messageId || i} className="p-2.5 border border-stone-200 rounded text-xs hover:bg-stone-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-stone-700">{m.msisdn || '-'}</span>
                      <Badge variant="outline" className={`text-[9px] ${
                        m.status === 'Delivered' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                        m.status === 'Failed' ? 'bg-red-50 text-red-600 border-red-200' :
                        m.status === 'Sent' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                        'bg-amber-50 text-amber-600 border-amber-200'
                      }`}>
                        {m.status || '?'}
                      </Badge>
                      {m.reason && m.reason !== m.status && (
                        <span className="text-[10px] text-stone-400">{m.reason}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-stone-400">
                      {m.messagePartCount > 1 && <Badge variant="outline" className="text-[9px]">{m.messagePartCount} SMS</Badge>}
                      <span>{m.submittedAt ? new Date(m.submittedAt).toLocaleString('es-ES', {day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-[10px] text-stone-400">
                    <span>{lang === 'es' ? 'De' : 'From'}: {m.originator || '-'}</span>
                    <span>{lang === 'es' ? 'Pais' : 'Country'}: {m.destinationCode || '-'}</span>
                    <span>{lang === 'es' ? 'Red' : 'Network'}: {m.networkUsed || '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REMINDERS TAB */}
        <TabsContent value="reminders" className="mt-4">
          <Card className="border-stone-200 rounded-sm shadow-none">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell size={16} className="text-amber-500" />
                {lang === 'es' ? 'Recordatorios automaticos de citas' : 'Automatic appointment reminders'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="reminder-enabled-toggle">
                <Switch checked={reminderCfg.enabled} onCheckedChange={v => setReminderCfg(p => ({ ...p, enabled: v }))} />
                <span className="text-sm font-medium text-stone-800">{lb.reminderEnabled}</span>
              </label>
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.hoursBefore}</Label>
                <Input data-testid="reminder-hours" type="number" min="1" max="168" value={reminderCfg.hours_before}
                  onChange={e => setReminderCfg(p => ({ ...p, hours_before: parseInt(e.target.value) || 24 }))}
                  className="max-w-[120px] h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.reminderMsg}</Label>
                <textarea data-testid="reminder-message" value={reminderCfg.message}
                  onChange={e => setReminderCfg(p => ({ ...p, message: e.target.value }))}
                  className="w-full min-h-[80px] text-xs border border-stone-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <p className="text-[10px] text-stone-400">{lb.tags}</p>
              </div>
              <Button onClick={handleSaveReminderConfig} className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1.5" data-testid="reminder-save-btn">
                {lb.saveConfig}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateSmsTemplateDialog open={showCreateTemplate} onClose={() => setShowCreateTemplate(false)}
        onCreated={() => { setShowCreateTemplate(false); loadTemplates(); }} lang={lang} lb={lb} />
    </div>
  );
}


function CreateSmsTemplateDialog({ open, onClose, onCreated, lang, lb }) {
  const [form, setForm] = useState({ name: '', body: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error(lang === 'es' ? 'Nombre y texto obligatorios' : 'Name and text required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/esendex/templates`, form, { withCredentials: true });
      toast.success(lb.templateCreated);
      setForm({ name: '', body: '' });
      onCreated();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-sms-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.createTemplate}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {lang === 'es' ? 'Crear una plantilla de SMS reutilizable' : 'Create a reusable SMS template'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.name} *</Label>
            <Input data-testid="sms-tpl-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={lang === 'es' ? 'Ej: Recordatorio cita' : 'E.g.: Appointment reminder'} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.body} *</Label>
            <textarea data-testid="sms-tpl-body" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              className="w-full min-h-[100px] text-xs border border-stone-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder={lang === 'es' ? 'Le recordamos su cita el dia: {fecha} a las: {hora}' : 'Reminder: your appointment on {fecha} at {hora}'} />
            <p className="text-[10px] text-stone-400">{lb.tags}</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="text-xs">{lb.cancel}</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="confirm-create-sms-template">
            {saving ? lb.loading : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
